
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { Shield } from "lucide-react";

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, isLoading, session, refreshSession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isVerifying, setIsVerifying] = useState(true);

  // Security: Enhanced authentication check 
  useEffect(() => {
    // Failsafe: never keep the user stuck verifying for too long
    let failsafe: ReturnType<typeof setTimeout> | null = null;

    const verifyAuthentication = () => {
      try {
        if (isLoading) {
          setIsVerifying(true);
          // End verifying after 4s even if something goes wrong
          failsafe = setTimeout(() => setIsVerifying(false), 4000);
          return;
        }

        if (!user) {
          setIsVerifying(false);
          navigate("/auth", { state: { from: location.pathname }, replace: true });
          return;
        }

        // User exists, allow access
        setIsVerifying(false);
      } catch (error) {
        console.error("Authentication verification error:", error);
        toast.error("Session verification failed");
        setIsVerifying(false);
        navigate("/auth", { replace: true });
      }
    };

    verifyAuthentication();

    return () => {
      if (failsafe) clearTimeout(failsafe);
    };
  }, [user, isLoading, navigate, location.pathname]);

  // Prioritize rendering when a user exists, even if auth is still finalizing
  if (user) {
    return (
      <div className="relative">
        {/* Security badge for protected routes */}
        <div className="fixed bottom-4 right-4 z-50 opacity-30 hover:opacity-100 transition-opacity">
          <div className="bg-gray-900 text-white text-xs rounded-full px-2 py-1 flex items-center">
            <Shield className="h-3 w-3 mr-1" />
            <span>Secured</span>
          </div>
        </div>
        {children}
      </div>
    );
  }

  // Show loading state while checking authentication
  if (isLoading || isVerifying) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-white to-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#00A300] mb-4"></div>
        <p className="text-gray-600 animate-pulse">Verifying secure access...</p>
      </div>
    );
  }

  // No user and not loading: render nothing, effect will redirect to /auth
  return null;
};

export default ProtectedRoute;
